import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { getPinnedAccounts } from '../../../utils/pinAccounts';
import { useAppSelector } from '../../../redux/store';
import type { Account } from '../../../redux/types';
import {
  getCurrentAccountId,
  getCurrentAccountName,
  getCurrentUserEmail,
  getLogoutUrl,
} from '../../../utils/tokenUtils';
import styles from './mobileHeader.module.scss';
import { deleteAccountToken, deleteUserToken } from '../../../utils/tokenServices';
import classes from '../userDropdown/userDropdown.module.scss';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import MobileLanguageSelector from './MobileLanguageSelector.tsx';
import { SignOutIcon } from '@phosphor-icons/react/dist/csr/SignOut';
import { UserCheckIcon } from '@phosphor-icons/react/dist/csr/UserCheck';
import { UserCirclePlusIcon } from '@phosphor-icons/react/dist/csr/UserCirclePlus';
import { UsersIcon } from '@phosphor-icons/react/dist/csr/Users';
import { UserSwitchIcon } from '@phosphor-icons/react/dist/csr/UserSwitch';
import { PushPinSimpleIcon } from '@phosphor-icons/react/dist/csr/PushPinSimple';

const {
  userMenuContainer,
  userMenuDivider,
  userMenuItem,
  iconPinnedAccount,
  iconLogout,
  iconAccount,
  iconUser,
  userMenuActive,
} = classes;

interface MobileUserDropDownProps {
  handleSidebar: () => void;
  isOpen: boolean;
}

const MobileUserDropDown: FC<MobileUserDropDownProps> = ({ handleSidebar, isOpen }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'userDropdown' });
  const [pinnedAccounts, setPinnedAccounts] = useState<{ accountId: number; name: string }[]>(getPinnedAccounts());
  const frameworkMode = useAppSelector(state => state.layout.frameworkMode);

  const isAccountMode = frameworkMode === 'ACCOUNT';
  const { pathname } = window.location;
  const getTitle = (account?: Account) => {
    const title = getCurrentUserEmail() ?? 'User';
    const accountId = account ? account.accountId : getCurrentAccountId();
    const accountName = account ? account.name : getCurrentAccountName();

    return (
      <div>
        <span>{title}</span>
        {frameworkMode === 'ACCOUNT' ? <span>{`${accountName} - ${accountId}`}</span> : null}
      </div>
    );
  };

  const title = getTitle();

  const onAccountChanges = () => {
    setPinnedAccounts(getPinnedAccounts());
    handleSidebar();
  };

  useEffect(() => {
    window.addEventListener('ACCOUNT_CHANGES', onAccountChanges);

    return () => window.removeEventListener('ACCOUNT_CHANGES', onAccountChanges);
  }, []);

  const onChangeAccount = (account: { accountId: number; name: string }) => {
    window.dispatchEvent(new CustomEvent('framework-change-Account', { detail: account }));
    handleSidebar();
  };

  const onRedirectToSelectAccount = () => {
    window.dispatchEvent(new CustomEvent('framework-redirect-SelectAccount'));
    handleSidebar();
  };

  const onRedirectToCreateAccount = () => {
    window.dispatchEvent(new CustomEvent('framework-redirect-CreateAccount'));
    handleSidebar();
  };

  const backToUserMode = () => {
    window.dispatchEvent(new CustomEvent('framework-back-to-UserMode'));
    handleSidebar();
  };

  const openProfile = () => {
    window.dispatchEvent(new CustomEvent('framework-open-profile'));
    handleSidebar();
  };

  const logout = () => {
    const logoutUrl = getLogoutUrl() ?? '/';
    deleteAccountToken();
    deleteUserToken();
    const opened = window.open(logoutUrl, '_blank');
    if (opened) {
      setTimeout(() => {
        opened.close();
        window.location.replace('/');
      }, 1000);
    }
    handleSidebar();
  };

  const menuItems = (
    <>
      {isAccountMode && (
        <div className={userMenuItem} onClick={backToUserMode}>
          <UserCheckIcon size={24} className={iconUser} />
          {t('returnTo')} {getCurrentUserEmail() ?? t('user')}
        </div>
      )}
      <div className={userMenuDivider}></div>
      <div
        className={cn(userMenuItem, { [userMenuActive]: pathname === '/create-account' })}
        onClick={onRedirectToCreateAccount}
      >
        <UserCirclePlusIcon size={24} className={iconAccount} />
        {t('createAccount')}
      </div>
      <div
        className={cn(userMenuItem, { [userMenuActive]: pathname === '/select-account' })}
        onClick={onRedirectToSelectAccount}
      >
        <UserSwitchIcon size={24} className={iconAccount} />
        {isAccountMode ? t('changeAccount') : t('selectAccount')}
      </div>
      {pinnedAccounts.map(account => (
        <div
          className={cn(userMenuItem, { [userMenuActive]: getCurrentAccountId() === account.accountId })}
          key={account.accountId}
          onClick={() => onChangeAccount(account)}
        >
          <PushPinSimpleIcon size={24} weight="fill" className={iconPinnedAccount} />
          {`${account.accountId} - ${account.name}`}
        </div>
      ))}
    </>
  );

  if (!isOpen) return null;

  return (
    <div data-testid="menu-container" className={styles.userSideBarWrapper}>
      <div className={userMenuContainer}>
        <div className={cn(userMenuItem, { [userMenuActive]: pathname === '/users' })} onClick={openProfile}>
          <UsersIcon size={24} className={iconUser} />
          <span className={styles.userTitle}>{title}</span>
        </div>
        {menuItems}
        <div className={userMenuDivider}></div>
        <MobileLanguageSelector />
        <div className={userMenuDivider}></div>
        <div className={userMenuItem} onClick={logout}>
          <SignOutIcon size={24} className={iconLogout} />
          {t('logout')}
        </div>
      </div>
    </div>
  );
};

export default MobileUserDropDown;
